/* MBG favourites — device/browser-local only, via localStorage. No
   customer account or server-side persistence: a signed-in customer on
   a different device, or the same device with site data cleared, won't
   see the same favourites. That's a deliberate, documented limit of
   this implementation, not a bug — there is no existing wishlist
   infrastructure in this theme/store to build a synced version on top
   of, and inventing a fake server-side store would be worse than being
   honest about the real constraint.

   Keyed by product HANDLE (not numeric ID) so the /favourites listing
   page (see the bottom of this file) can fetch each saved product back
   via the public, unauthenticated /products/{handle}.js endpoint. */
(function () {
  'use strict';

  var STORAGE_KEY = 'mbg_favourites';

  function readFavourites() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeFavourites(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      // Private browsing / storage disabled / quota exceeded — favouriting
      // just won't persist this session. Nothing else in the page depends
      // on it, so this fails silently rather than breaking anything else.
    }
  }

  function setButtonState(button, saved) {
    var title = button.getAttribute('data-product-title');
    var suffix = title ? ' ' + title : '';
    button.classList.toggle('mbg-favourite-button--saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', (saved ? 'Remove' : 'Add') + suffix + (saved ? ' from favourites' : ' to favourites'));
  }

  function syncAllButtons() {
    var favourites = readFavourites();
    document.querySelectorAll('[data-mbg-favourite]').forEach(function (button) {
      var handle = button.getAttribute('data-product-handle');
      setButtonState(button, favourites.indexOf(handle) > -1);
    });
    syncHeaderCount(favourites);
  }

  function syncHeaderCount(favourites) {
    favourites = favourites || readFavourites();
    var countEl = document.querySelector('[data-mbg-favourites-count]');
    if (!countEl) {
      return;
    }
    var span = countEl.querySelector('span');
    if (span) {
      span.textContent = favourites.length;
    }
    countEl.hidden = favourites.length === 0;
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-mbg-favourite]');
    if (!button) {
      return;
    }
    event.preventDefault();

    var handle = button.getAttribute('data-product-handle');
    var favourites = readFavourites();
    var index = favourites.indexOf(handle);
    var nowSaved;

    if (index > -1) {
      favourites.splice(index, 1);
      nowSaved = false;
    } else {
      favourites.push(handle);
      nowSaved = true;
    }

    writeFavourites(favourites);

    document.querySelectorAll('[data-mbg-favourite][data-product-handle="' + handle + '"]').forEach(function (matching) {
      setButtonState(matching, nowSaved);
    });
    syncHeaderCount(favourites);

    if (window.mbgFavouritesPage) {
      window.mbgFavouritesPage.onChange();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllButtons);
  } else {
    syncAllButtons();
  }

  /* --- /favourites listing page -----------------------------------------
     Only does anything if the page actually has the list container (see
     templates/page.favourites.liquid) — everywhere else this whole block
     is a no-op. Fetches each saved handle's real product data from
     Shopify's own public product JSON endpoint and renders a compact
     card; never fabricates a product that doesn't exist (a 404 for a
     handle — e.g. the product was deleted since being favourited — is
     just skipped, and silently removed from the saved list). */
  var listEl = document.querySelector('[data-mbg-favourites-list]');
  if (!listEl) {
    return;
  }

  var emptyEl = document.querySelector('[data-mbg-favourites-empty]');

  /* theme.Currency.formatMoney (assets/theme.js) + theme.moneyFormat (the
     shop's own real money_format string, set in layout/theme.liquid) is
     the same formatter every other price on the site already uses — real
     currency symbol and separators from Shopify's own settings, not a
     hand-rolled "amount + currency code" string. */
  function money(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function escapeAttr(value) {
    return String(value || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function renderCard(product) {
    var variant = product.variants && product.variants[0];
    /* variant.price from /products/{handle}.js is already in the shop's
       minor currency unit (e.g. 4500 = £45.00) — the same unit money()
       expects, so no extra *100 here. That extra multiplication was the
       exact cause of £45 rendering as £4500 (4500 * 100 / 100... no: it
       produced 450000 / 100 = 4500.00). */
    var price = variant ? money(variant.price) : '';
    /* product.images from this same endpoint is an array of plain URL
       strings, not objects — image.src on a string is always undefined,
       which is why the <img> tag rendered with src="undefined" and no
       image ever showed. image IS the URL. */
    var image = product.images && product.images[0];
    var titleAttr = escapeAttr(product.title);

    var card = document.createElement('div');
    card.className = 'mbg-favourites-card';
    card.innerHTML =
      '<button type="button" class="mbg-favourite-button mbg-favourite-button--saved mbg-favourites-card__remove" data-mbg-favourite data-product-handle="' + product.handle + '" data-product-title="' + titleAttr + '" aria-pressed="true" aria-label="Remove ' + titleAttr + ' from favourites">' +
        '<svg class="mbg-favourite-button__icon mbg-favourite-button__icon--filled" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-10-9.3C.5 8 1.8 4.5 5 3.6c2.2-.6 4.4.3 5.6 2.1l1.4 2 1.4-2c1.2-1.8 3.4-2.7 5.6-2.1 3.2.9 4.5 4.4 3 7.6-2.5 4.7-10 9.3-10 9.3z"/></svg>' +
      '</button>' +
      '<a href="/products/' + product.handle + '" class="mbg-favourites-card__media">' +
        (image ? '<img src="' + image + '" alt="' + titleAttr + '" loading="lazy" width="400" height="500">' : '') +
      '</a>' +
      '<a href="/products/' + product.handle + '" class="mbg-favourites-card__title">' + titleAttr + '</a>' +
      '<span class="mbg-favourites-card__price">' + price + '</span>' +
      '<span class="mbg-favourites-card__availability' + (product.available ? '' : ' mbg-favourites-card__availability--sold-out') + '">' + (product.available ? 'Available' : 'Sold Out') + '</span>' +
      '<a href="/products/' + product.handle + '" class="mbg-favourites-card__view">View product</a>';
    return card;
  }

  function loadFavourites() {
    var handles = readFavourites();
    listEl.innerHTML = '';

    if (!handles.length) {
      if (emptyEl) {
        emptyEl.hidden = false;
      }
      return;
    }
    if (emptyEl) {
      emptyEl.hidden = true;
    }

    var validHandles = [];
    var fetches = handles.map(function (handle) {
      return fetch('/products/' + handle + '.js')
        .then(function (response) {
          if (!response.ok) {
            return null;
          }
          return response.json();
        })
        .catch(function () {
          return null;
        })
        .then(function (product) {
          if (product) {
            validHandles.push(handle);
          }
          return product;
        });
    });

    Promise.all(fetches).then(function (products) {
      products.forEach(function (product) {
        if (product) {
          listEl.appendChild(renderCard(product));
        }
      });
      if (!listEl.children.length && emptyEl) {
        emptyEl.hidden = false;
      }
      // A handle that 404s (deleted/unpublished product) is quietly
      // dropped from the saved list rather than shown as a dead card
      // every time this page loads.
      if (validHandles.length !== handles.length) {
        writeFavourites(validHandles);
      }
    });
  }

  window.mbgFavouritesPage = {
    onChange: loadFavourites
  };

  loadFavourites();
})();
